import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Separator } from "design-system/atoms/Separator"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { renderLiquid } from "lopermut"
import { FlaskConical, List } from "lucide-react"
import { useTestZendeskTicketMutation } from "modules/settings/hooks/zendesk/useTestZendeskTicketMutation"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { WorkflowNodeSchema, CreateZendeskTicketConfigSchema, ZendeskPriorityEnum } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { useEventPayload } from "../../hooks/useEventPayload"

type TicketPreviewProps = {
  formValues: z.infer<typeof CreateZendeskTicketConfigSchema>
  eventPayload: Record<string, unknown>
}

function TicketPreview({ formValues, eventPayload }: TicketPreviewProps) {
  const { t } = useTranslation()

  const customerValues = {
    name: {
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.requester.name"),
      value: t(
        "campaigns.action.availableActions.CreateZendeskTicket.config.requester.namePlaceholder",
      ),
    },
    email: {
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.requester.email"),
      value: t(
        "campaigns.action.availableActions.CreateZendeskTicket.config.requester.emailPlaceholder",
      ),
    },
    phone: {
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.requester.phone"),
      value: t(
        "campaigns.action.availableActions.CreateZendeskTicket.config.requester.phonePlaceholder",
      ),
    },
  }

  const values = {
    subject: {
      field: "subject",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.subject"),
      value: formValues.subject,
      showValue: true,
    },
    comment: {
      field: "comment",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.comment"),
      value: formValues.comment,
      showValue: true,
    },
    brandId: {
      field: "brandId",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.brandId"),
      value: formValues.brandId,
      showValue: true,
    },
    ticketFormId: {
      field: "ticketFormId",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.ticketFormId"),
      value: formValues.ticketFormId,
      showValue: true,
    },
    customFields: {
      field: "customFields",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.customFields"),
      value: formValues.customFields,
      showValue: false,
    },
    tags: {
      field: "tags",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.tags"),
      value: formValues.tags,
      showValue: true,
    },
    priority: {
      field: "priority",
      copy: t("campaigns.action.availableActions.CreateZendeskTicket.config.priority"),
      value: formValues.priority,
      showValue: false,
    },
  }

  return (
    <Card className="flex flex-col gap-2">
      <Accordion type="single" collapsible className="w-full text-left">
        <AccordionItem value="preview">
          <AccordionTrigger>
            {t("campaigns.action.availableActions.CreateZendeskTicket.config.preview")}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-0">
              <span className="text-sm text-muted-foreground">
                {t("campaigns.action.availableActions.CreateZendeskTicket.config.requester.title")}
              </span>
              {Object.entries(customerValues).map(([key, { copy, value }]) => (
                <div key={key} className="flex flex-row gap-0 items-center text-sm">
                  <span>{copy}</span>
                  <span>{":"}&nbsp;</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
            {Object.entries(values).map(([key, { copy, field, value, showValue }]) => {
              let displayedValue: string | number
              if (key === "priority") {
                displayedValue = t(
                  `campaigns.action.availableActions.CreateZendeskTicket.config.priorities.${value ?? "none"}`,
                )
              } else {
                try {
                  displayedValue = renderLiquid(value ? String(value) : "", eventPayload)
                } catch {
                  displayedValue = ""
                }
              }

              return (
                <div key={key} className="flex flex-col gap-0">
                  <span className="text-sm text-muted-foreground">{copy}</span>
                  <span
                    className={cn(
                      "text-sm",
                      showValue && "font-semibold",
                      field === "customFields" &&
                        "bg-muted p-2 font-mono rounded-md whitespace-pre-wrap",
                    )}
                  >
                    {displayedValue && displayedValue.toString().length > 0 ? displayedValue : "-"}
                  </span>
                </div>
              )
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  )
}

type CreateZendeskTicketConfigurationProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function CreateZendeskTicketConfiguration({
  nodeIndex,
  node,
}: CreateZendeskTicketConfigurationProps) {
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })

  const { mutateAsync: testZendeskTicket, isPending: isTestZendeskTicketPending } =
    useTestZendeskTicketMutation()

  async function handleTestZendeskTicket() {
    const formConfig = form.getValues(`nodes.${nodeIndex}.config`)
    const result = CreateZendeskTicketConfigSchema.safeParse(formConfig)

    if (!result.success) {
      toast.error(
        t("campaigns.action.availableActions.CreateZendeskTicket.config.test.notCompletedFields"),
      )
      return
    }

    await testZendeskTicket({
      data: formConfig as z.input<typeof CreateZendeskTicketConfigSchema>,
      eventPayload: eventPayload ?? {},
    })

    toast.success(t("campaigns.action.availableActions.CreateZendeskTicket.config.test.success"))
    setTestDialogOpen(false)
  }

  useEffect(() => {
    const result = CreateZendeskTicketConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, {
        subject: "",
        comment: null,
        brandId: null,
        ticketFormId: null,
        customFields: null,
        tags: null,
        priority: null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const subject = form.watch(`nodes.${nodeIndex}.config.subject`) || ""
  const comment = form.watch(`nodes.${nodeIndex}.config.comment`) || null
  const brandId = form.watch(`nodes.${nodeIndex}.config.brandId`) || null
  const ticketFormId = form.watch(`nodes.${nodeIndex}.config.ticketFormId`) || null
  const customFields = form.watch(`nodes.${nodeIndex}.config.customFields`) || null
  const tags = form.watch(`nodes.${nodeIndex}.config.tags`) || null
  const priority = form.watch(`nodes.${nodeIndex}.config.priority`) || null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 justify-start items-start">
        <span className="text-sm text-muted-foreground">
          <Trans
            i18nKey="campaigns.action.availableActions.CreateZendeskTicket.config.description"
            components={{
              b: <span className="font-medium" />,
              a: (
                <a
                  href="https://shopify.dev/docs/api/liquid"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:opacity-80"
                />
              ),
            }}
          />
        </span>
      </div>
      <TicketPreview
        formValues={{
          subject,
          comment,
          brandId,
          ticketFormId,
          customFields,
          tags,
          priority,
        }}
        eventPayload={eventPayload ?? {}}
      />
      <div className="flex flex-row gap-2 justify-between w-full">
        <Button
          variant="testing"
          disabled={!form.formState.isValid}
          onClick={() => setTestDialogOpen(true)}
          className="flex-1"
        >
          <FlaskConical className="size-4" />
          <span>{t("campaigns.action.availableActions.CreateZendeskTicket.config.test.cta")}</span>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              <List className="size-4" />
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.data")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2 sm:w-[560px] w-full">
            <span className="text-base">
              <Trans
                i18nKey="campaigns.action.availableActions.CreateZendeskTicket.config.availableData"
                components={{ b: <span className="font-medium" /> }}
                values={{ eventName }}
              />
            </span>
            <MarkdownRenderer
              className="bg-muted rounded-md overflow-auto max-h-96 border"
              codeClassName="border-none"
            >{`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}</MarkdownRenderer>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogTitle>
            {t("campaigns.action.availableActions.CreateZendeskTicket.config.test.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.action.availableActions.CreateZendeskTicket.config.test.warning")}
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={isTestZendeskTicketPending}
            >
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleTestZendeskTicket} disabled={isTestZendeskTicketPending}>
              {t("shared.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Separator />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.subject`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.subject")}
              <span className="text-destructive">{" *"}</span>
            </FormLabel>
            <FormControl>
              <Input
                value={subject}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.subject`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.subjectPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.comment`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.comment")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={comment}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.comment`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.commentPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.brandId`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.brandId")}
            </FormLabel>
            <FormControl>
              <Input
                value={brandId}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.brandId`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.brandIdPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.ticketFormId`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.ticketFormId")}
            </FormLabel>
            <FormControl>
              <Input
                value={ticketFormId}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.ticketFormId`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.ticketFormIdPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.customFields`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.customFields")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={customFields}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.customFields`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.customFieldsPlaceholder",
                )}
              />
            </FormControl>
            <FormDescription>
              {t(
                "campaigns.action.availableActions.CreateZendeskTicket.config.customFieldsDescription",
              )}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.tags`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.tags")}
            </FormLabel>
            <FormControl>
              <Input
                value={tags}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.tags`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateZendeskTicket.config.tagsPlaceholder",
                )}
              />
            </FormControl>
            <FormDescription>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.tagsDescription")}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.priority`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateZendeskTicket.config.priority")}
            </FormLabel>
            <FormControl>
              <Select
                value={priority ?? "none"}
                onValueChange={(value) => {
                  const newValue = value === "none" ? null : value
                  form.setValue(`nodes.${nodeIndex}.config.priority`, newValue, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "campaigns.action.availableActions.CreateZendeskTicket.config.priorities.none",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-muted-foreground">
                    {t(
                      "campaigns.action.availableActions.CreateZendeskTicket.config.priorities.none",
                    )}
                  </SelectItem>
                  {Object.values(ZendeskPriorityEnum).map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {t(
                        `campaigns.action.availableActions.CreateZendeskTicket.config.priorities.${priority}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
